import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EventIcon from '@mui/icons-material/Event';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import moment from 'moment';
import { tokens } from '../../../../global/theme/tokens';
import DateLabel from '../../../label/DateLabel';
import { useTimeTracking } from '../../../time-tracking/AppTimeProvider';
import { renderHours } from '../../../../scenes/time-tracking/worktime';
import WorkPackageTaskList from './WorkPackageTaskList';

const ProjectTaskWorkPackage = ({
  project,
  workPackage,
  filter,
  onLoadingChange,
  onStatsChange,
  allowCreate = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { clockIn, clockOut, activeProject, secondsIn } = useTimeTracking();

  const [isOpen, setIsOpen] = useState(false);

  const {
    id,
    title,
    icon,
    endDate,
    status,
    isTimeTrackable,
    allowTimeTracking,
    taskStats,
  } = workPackage;

  const totalTasks = taskStats?.totalTasks || 0;
  const doneTasks = taskStats?.doneTasks || 0;

  const isActive = activeProject?.activeWorkPackage?.id === id;

  // same condition as the tree view: the work package is trackable and the API
  // allows the current user to track time on it
  const showTimeTracking = Boolean(isTimeTrackable) && allowTimeTracking;

  // status 0 = running: only a running package can run out of time
  const isAfterDeadline =
    status === 0 && endDate && moment(endDate).isBefore(moment(), 'day');

  return (
    <Box
      className="rounded-xl overflow-hidden"
      sx={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.text}12`,
      }}
    >
      <Box
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-wrap items-center gap-2 px-2 py-1.5 cursor-pointer"
        sx={{ ':hover': { backgroundColor: `${colors.text}06` } }}
      >
        <IconButton size="small" onClick={() => setIsOpen(!isOpen)}>
          <KeyboardArrowRightIcon
            sx={{
              fontSize: '18px',
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>

        {/* icon before WP */}
        {icon?.temporaryUrl && (
          <img
            src={icon.temporaryUrl}
            alt=""
            className="w-6 h-6 object-contain shrink-0"
          />
        )}

        {/* flex-basis 0: the row wraps, so a long title has to be cut off
            instead of pushing the counters onto the next line */}
        <Box
          className="flex flex-col md:flex-row md:items-baseline gap-x-3"
          sx={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}
        >
          <Typography
            fontWeight={500}
            noWrap
            title={title}
            sx={{
              fontSize: { xs: '14px', sm: '15px' },
              // while the row is a column the flex shorthand would zero the height
              flex: { xs: '0 1 auto', md: '1 1 0%' },
              minWidth: 0,
            }}
          >
            {title}
          </Typography>

          {endDate && (
            <Box
              className="flex items-center gap-1 shrink-0"
              sx={{
                color: isAfterDeadline ? colors.error : 'inherit',
                opacity: isAfterDeadline ? 1 : 0.6,
              }}
            >
              <EventIcon sx={{ fontSize: '12px' }} />
              <Typography fontSize="11px">
                {`${t('End')} `}
                <DateLabel date={endDate} />
              </Typography>
            </Box>
          )}
        </Box>

        {/* done of all tasks, updated in place while tasks are ticked off */}
        {Boolean(totalTasks) && (
          <Box className="flex flex-col items-end shrink-0">
            <Typography fontSize="9px" className="uppercase opacity-50">
              {t('Tasks')}
            </Typography>
            <Typography
              fontSize="12px"
              fontWeight={500}
              className="monospace"
              sx={{ color: doneTasks === totalTasks ? colors.success : null }}
            >
              {`${doneTasks}/${totalTasks}`}
            </Typography>
          </Box>
        )}

        {showTimeTracking && (
          <Box
            // on mobile the button gets its own row instead of squeezing the title
            className="shrink-0 w-full md:w-auto flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            {isActive ? (
              <Button
                onClick={clockOut}
                variant="outlined"
                color="error"
                size="small"
                className="flex items-center gap-2"
                sx={{ borderRadius: '8px' }}
              >
                <Typography className="monospace" fontSize="13px">
                  {`${renderHours(Math.round(secondsIn / 60))} h`}
                </Typography>
                {t('Clock Out')}
                <StopIcon sx={{ fontSize: '18px' }} />
              </Button>
            ) : (
              <Button
                onClick={() => clockIn(project, workPackage)}
                variant="outlined"
                color="success"
                size="small"
                startIcon={<PlayArrowIcon />}
                sx={{ borderRadius: '8px' }}
              >
                {t('Clock In')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* unmountOnExit: collapsing drops the tasks, expanding refetches them */}
      <Collapse in={isOpen} unmountOnExit>
        <Box
          className="px-3 py-3"
          sx={{ borderTop: `1px solid ${colors.text}12` }}
        >
          <WorkPackageTaskList
            project={project}
            workPackage={workPackage}
            filter={filter}
            onLoadingChange={onLoadingChange}
            onStatsChange={onStatsChange}
            allowCreate={allowCreate}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProjectTaskWorkPackage;
